import {useEffect, useState, useRef} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {getPublicReport} from '../Reducers/actions';
import html2canvas from 'html2canvas';
import {jsPDF} from 'jspdf';
import './rapport.css';

const PublicReportPage = () => {
  const dispatch = useDispatch();
  const {token} = useParams();
  const {loading} = useSelector(state => state.zwembadinspecties);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  const reportRef = useRef();
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    if (token) {
      dispatch(getPublicReport(token)).then(response => {
        if (response && response.success && response.result) {
          setReportData(response.result);
        } else {
          setError(response?.message || 'Rapport niet gevonden of link is verlopen');
        }
      });
    }
  }, [dispatch, token]);

  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handlePrint = () => {
    window.print();
  };

  /**
   * Voer async taken uit in batches om rate limiting te voorkomen
   */
  const runInBatches = async (tasks, batchSize = 5) => {
    const results = [];
    for (let i = 0; i < tasks.length; i += batchSize) {
      const batch = tasks.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map(fn => fn()));
      results.push(...batchResults);
    }
    return results;
  };

  /**
   * Pre-load alle afbeeldingen in een element zodat html2canvas ze kan renderen
   */
  const preloadImages = async (element) => {
    const imgs = Array.from(element.querySelectorAll('img'));
    const toLoad = imgs.filter(img => !(img.complete && img.naturalWidth > 0));
    if (toLoad.length === 0) return;

    const tasks = toLoad.map(img => () => new Promise((resolve) => {
      const newImg = new Image();
      newImg.crossOrigin = 'anonymous';
      newImg.onload = () => {
        img.src = newImg.src;
        resolve();
      };
      newImg.onerror = () => {
        console.warn('Preload failed for:', img.src);
        resolve();
      };
      newImg.src = img.src;
    }));
    await runInBatches(tasks, 3);
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current) return;

    setDownloadingPDF(true);
    try {
      const reportElement = reportRef.current;

      // Pre-load alle afbeeldingen voordat we beginnen met renderen
      await preloadImages(reportElement);

      reportElement.classList.add('pdf-rendering');

      const canvas = await html2canvas(reportElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgWidth = pdfWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;
      let page = 0;

      while (heightLeft >= 0) {
        if (page > 0) {
          pdf.addPage();
        }
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.95),
          'JPEG',
          margin,
          position - page * (pdfHeight - 2 * margin),
          imgWidth,
          imgHeight,
        );
        heightLeft -= pdfHeight - 2 * margin;
        page++;
      }

      const company = reportData?.inspection?.company_id?.name || 'Rapport';
      const date = formatDate(reportData?.inspection?.inspection_date);
      pdf.save(`Inspectie_Rapport_${company}_${date}.pdf`.replace(/\s+/g, '_'));

      reportElement.classList.remove('pdf-rendering');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Fout bij genereren PDF');
    } finally {
      setDownloadingPDF(false);
    }
  };

  const openLightbox = imageUrl => {
    setLightboxImage(imageUrl);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
  };

  if (error) {
    return (
      <div className="public-report-container">
        <div className="public-report-error">
          <div className="error-content">
            <i className="fas fa-exclamation-triangle error-icon"></i>
            <h2>Rapport niet beschikbaar</h2>
            <p>{error}</p>
            <p className="error-hint">
              Mogelijk is de link verlopen of is het rapport nog niet beschikbaar.
              Neem contact op met de inspecteur voor een nieuwe link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading || !reportData) {
    return (
      <div className="public-report-container">
        <div className="public-report-loading">
          <div className="spinner-border" role="status">
            <span className="sr-only">Laden...</span>
          </div>
          <p>Rapport wordt geladen...</p>
        </div>
      </div>
    );
  }

  const inspection = reportData.inspection;
  const company = inspection.company_id || {};
  const template = inspection.template_id || {};
  const inspector = inspection.inspector_id || {};
  const bedrijf = inspection.bedrijf || {};

  // Groepeer resultaten per hoofdstuk
  const chapters = template.chapters || [];
  const resultsByChapter = {};

  chapters.forEach(chapter => {
    resultsByChapter[chapter._id] = reportData.results.filter(result => {
      const itemChapterId = result.template_item_id?.chapter_id;
      return (
        itemChapterId === chapter._id ||
        String(itemChapterId) === String(chapter._id)
      );
    });
  });

  // Items zonder hoofdstuk
  const itemsWithoutChapter = reportData.results.filter(result => {
    const itemChapterId = result.template_item_id?.chapter_id;
    return (
      !itemChapterId ||
      !chapters.find(
        c => c._id === itemChapterId || String(c._id) === String(itemChapterId),
      )
    );
  });

  const renderValue = (result, item) => {
    // Skip objecten/arrays (bijv. schaaldelen data)
    if (typeof result.value === 'object' && result.value !== null) {
      return <span className="text-muted">-</span>;
    }

    if (!result.value && result.value !== 0 && result.value !== false) {
      return <span className="text-muted">-</span>;
    }

    if (item?.input_type === 'checkbox') {
      return result.value === 'true' || result.value === true ? (
        <span className="badge badge-success">Ja</span>
      ) : (
        <span className="badge badge-secondary">Nee</span>
      );
    }

    if (item?.input_type === 'select' && item.options) {
      const option = item.options.find(o => o.value === result.value);
      return option?.label || String(result.value);
    }

    return String(result.value);
  };

  return (
    <div className="public-report-container">
      {/* Header met logo */}
      <div className="public-report-header no-print">
        <div className="header-content">
          <div className="company-info">
            <img src="/brand/logo_small.png" alt="Zwembadinspecties" className="company-logo" />
            <div className="company-details">
              <h1>Zwembadinspecties</h1>
              <p>Inspectie Rapport</p>
            </div>
          </div>
          <div className="action-buttons">
            <button
              className="btn btn-primary"
              onClick={handleDownloadPDF}
              disabled={downloadingPDF}>
              {downloadingPDF ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Downloaden...
                </>
              ) : (
                <>
                  <i className="fas fa-download mr-2"></i>
                  Download PDF
                </>
              )}
            </button>
            <button className="btn btn-info" onClick={handlePrint}>
              <i className="fas fa-print mr-2"></i>
              Afdrukken
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox voor foto's */}
      {lightboxImage && (
        <div className="photo-lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img src={lightboxImage} alt="Foto" />
            <button className="close-button" onClick={closeLightbox}>
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Rapport content */}
      <div ref={reportRef} className="report-container public-report">
        {/* Pagina 1: Voorblad */}
        <div className="report-page cover-page">
          <div className="cover-content">
            <img src="/brand/logo_small.png" alt="Zwembadinspecties" className="cover-logo" />
            <h1 className="report-title">Inspectie Rapport</h1>
            <h2 className="company-name">{company.name}</h2>
            <p className="company-address">
              {company.street} {company.house_number}
              <br />
              {company.postal_code} {company.city}
            </p>
            <div className="cover-details">
              <div className="detail-item">
                <span className="label">Inspectie type:</span>
                <span className="value">{template.name}</span>
              </div>
              <div className="detail-item">
                <span className="label">Categorie:</span>
                <span className="value">{template.category}</span>
              </div>
              <div className="detail-item">
                <span className="label">Inspectiedatum:</span>
                <span className="value">{formatDate(inspection.inspection_date)}</span>
              </div>
              <div className="detail-item">
                <span className="label">Inspecteur:</span>
                <span className="value">
                  {inspector.voornaam} {inspector.achternaam}
                </span>
              </div>
              <div className="detail-item">
                <span className="label">Status:</span>
                <span className={`badge badge-${inspection.status === 'approved' ? 'success' : inspection.status === 'completed' ? 'info' : 'secondary'}`}>
                  {inspection.status === 'approved'
                    ? 'Goedgekeurd'
                    : inspection.status === 'completed'
                    ? 'Afgerond'
                    : inspection.status}
                </span>
              </div>
            </div>
            {inspection.report_photo?.image && (
              <div className="cover-photo">
                <img
                  src={inspection.report_photo.thumbnail?.medium || inspection.report_photo.image}
                  alt="Rapport foto"
                  onClick={() => openLightbox(inspection.report_photo.thumbnail?.large || inspection.report_photo.image)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Introductie als aanwezig */}
        {template.introduction && (
          <div className="report-page">
            <h2 className="section-title">Introductie</h2>
            <div
              className="introduction-content"
              dangerouslySetInnerHTML={{__html: template.introduction}}
            />
          </div>
        )}

        {/* Hoofdstukken met resultaten */}
        {chapters
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((chapter, chapterIndex) => {
            const chapterResults = resultsByChapter[chapter._id] || [];
            if (chapterResults.length === 0) return null;

            return (
              <div key={chapter._id} className="report-page">
                <h2 className="section-title">
                  {chapterIndex + 1}. {chapter.title}
                </h2>
                {chapter.description && (
                  <p className="chapter-description">{chapter.description}</p>
                )}

                <table className="findings-table">
                  <thead>
                    <tr>
                      <th style={{width: '40%'}}>Item</th>
                      <th style={{width: '20%'}}>Resultaat</th>
                      <th style={{width: '30%'}}>Opmerkingen</th>
                      <th style={{width: '10%'}}>Foto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chapterResults.map((result, index) => {
                      const item = result.template_item_id;
                      const hasDeviation = result.has_deviation;

                      return (
                        <tr
                          key={result._id || index}
                          className={hasDeviation ? 'row-with-deviation' : ''}>
                          <td>
                            <strong>{item?.question || '-'}</strong>
                            {item?.description && (
                              <div className="item-description">{item.description}</div>
                            )}
                          </td>
                          <td>
                            {renderValue(result, item)}
                            {hasDeviation && (
                              <span
                                className={`deviation-indicator deviation-${result.deviation_severity}`}
                                title={`Afwijking: ${result.deviation_severity}`}>
                                ⚠
                              </span>
                            )}
                          </td>
                          <td>{(typeof result.remarks === 'string' ? result.remarks : null) || '-'}</td>
                          <td>
                            {result.attachments && result.attachments.length > 0 ? (
                              <div className="photo-thumbnails">
                                {result.attachments.map((photo, photoIndex) => (
                                  <img
                                    key={photoIndex}
                                    src={photo.thumbnail?.medium || photo.url}
                                    alt={`Foto ${photoIndex + 1}`}
                                    className="photo-thumb"
                                    onClick={() => openLightbox(photo.thumbnail?.large || photo.url)}
                                  />
                                ))}
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}

        {/* Items zonder hoofdstuk */}
        {itemsWithoutChapter.length > 0 && (
          <div className="report-page">
            <h2 className="section-title">Overige Items</h2>
            <table className="findings-table">
              <thead>
                <tr>
                  <th style={{width: '40%'}}>Item</th>
                  <th style={{width: '20%'}}>Resultaat</th>
                  <th style={{width: '30%'}}>Opmerkingen</th>
                  <th style={{width: '10%'}}>Foto</th>
                </tr>
              </thead>
              <tbody>
                {itemsWithoutChapter.map((result, index) => {
                  const item = result.template_item_id;
                  const hasDeviation = result.has_deviation;

                  return (
                    <tr
                      key={result._id || index}
                      className={hasDeviation ? 'row-with-deviation' : ''}>
                      <td>
                        <strong>{item?.question || '-'}</strong>
                      </td>
                      <td>
                        {renderValue(result, item)}
                        {hasDeviation && (
                          <span
                            className={`deviation-indicator deviation-${result.deviation_severity}`}>
                            ⚠
                          </span>
                        )}
                      </td>
                      <td>{(typeof result.remarks === 'string' ? result.remarks : null) || '-'}</td>
                      <td>
                        {result.attachments && result.attachments.length > 0 ? (
                          <div className="photo-thumbnails">
                            {result.attachments.map((photo, photoIndex) => (
                              <img
                                key={photoIndex}
                                src={photo.thumbnail?.medium || photo.url}
                                alt={`Foto ${photoIndex + 1}`}
                                className="photo-thumb"
                                onClick={() => openLightbox(photo.thumbnail?.large || photo.url)}
                              />
                            ))}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Algemene opmerkingen */}
        {inspection.general_remarks && (
          <div className="report-page">
            <h2 className="section-title">Algemene Opmerkingen</h2>
            <div className="general-remarks">
              <p>{inspection.general_remarks}</p>
            </div>
          </div>
        )}

        {/* Handtekening */}
        {inspection.signature_url && (
          <div className="report-page signature-page">
            <h2 className="section-title">Ondertekening</h2>
            <div className="signature-content">
              <img
                src={inspection.signature_url}
                alt="Handtekening"
                className="signature-image"
              />
              <p className="signature-date">
                Getekend op: {formatDate(inspection.end_time || inspection.inspection_date)}
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="report-footer">
          <p>
            Gegenereerd op: {new Date().toLocaleDateString('nl-NL')} | Zwembadinspecties.nl
          </p>
        </div>
      </div>

      {/* Stijlen voor publiek rapport */}
      <style>{`
        .public-report-container {
          min-height: 100vh;
          background-color: #f5f5f5;
        }

        .public-report-header {
          background: white;
          color: #333;
          padding: 20px 40px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          border-bottom: 3px solid #0088b4;
        }

        .public-report-header .header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .public-report-header .company-info {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .public-report-header .company-logo {
          height: 50px;
          width: auto;
        }

        .public-report-header h1 {
          margin: 0;
          font-size: 1.5rem;
          color: #0088b4;
        }

        .public-report-header p {
          margin: 0;
          color: #666;
        }

        .public-report-header .action-buttons {
          display: flex;
          gap: 10px;
        }

        .public-report-loading,
        .public-report-error {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
          text-align: center;
          padding: 40px;
        }

        .public-report-error .error-content {
          background: white;
          padding: 60px;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          max-width: 500px;
        }

        .public-report-error .error-icon {
          font-size: 4rem;
          color: #dc3545;
          margin-bottom: 20px;
        }

        .public-report-error h2 {
          margin-bottom: 15px;
          color: #333;
        }

        .public-report-error .error-hint {
          margin-top: 20px;
          color: #666;
          font-size: 0.9rem;
        }

        .public-report {
          max-width: 1000px;
          margin: 30px auto;
          padding: 0 20px 40px;
        }

        .cover-page {
          background: white;
          border-radius: 12px;
          padding: 60px;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }

        .cover-page .cover-logo {
          max-height: 80px;
          margin-bottom: 30px;
        }

        .cover-page .report-title {
          font-size: 2.5rem;
          color: #0088b4;
          margin-bottom: 20px;
        }

        .cover-page .company-name {
          font-size: 1.8rem;
          margin-bottom: 10px;
        }

        .cover-page .company-address {
          color: #666;
          margin-bottom: 30px;
        }

        .cover-page .cover-details {
          text-align: left;
          max-width: 400px;
          margin: 0 auto 30px;
        }

        .cover-page .detail-item {
          display: flex;
          justify-content: space-between;
          padding: 10px 0;
          border-bottom: 1px solid #eee;
        }

        .cover-page .detail-item .label {
          color: #666;
        }

        .cover-page .cover-photo {
          margin-top: 30px;
        }

        .cover-page .cover-photo img {
          max-width: 100%;
          max-height: 300px;
          border-radius: 8px;
          cursor: pointer;
        }

        .report-page {
          background: white;
          border-radius: 12px;
          padding: 40px;
          margin-bottom: 30px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }

        .section-title {
          color: #0088b4;
          border-bottom: 2px solid #0088b4;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }

        .chapter-description {
          color: #666;
          font-style: italic;
          margin-bottom: 20px;
        }

        .findings-table {
          width: 100%;
          border-collapse: collapse;
        }

        .findings-table th,
        .findings-table td {
          padding: 12px;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: top;
        }

        .findings-table th {
          background-color: #f8f9fa;
          font-weight: 600;
        }

        .findings-table .row-with-deviation {
          background-color: #fff3cd;
        }

        .item-description {
          font-size: 0.85rem;
          color: #666;
          margin-top: 5px;
        }

        .deviation-indicator {
          margin-left: 8px;
          font-size: 1.2rem;
        }

        .deviation-low { color: #ffc107; }
        .deviation-medium { color: #fd7e14; }
        .deviation-high { color: #dc3545; }

        .photo-thumb {
          max-width: 180px;
          max-height: 180px;
          object-fit: cover;
          border-radius: 4px;
          cursor: pointer;
          transition: transform 0.2s;
          display: block;
        }

        .photo-thumb:hover {
          transform: scale(1.05);
        }

        .photo-thumbnails {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }

        .more-photos {
          font-size: 0.8rem;
          color: #666;
        }

        .general-remarks {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
        }

        .signature-content {
          text-align: center;
        }

        .signature-image {
          max-width: 300px;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 10px;
          background: white;
        }

        .signature-date {
          margin-top: 15px;
          color: #666;
        }

        .report-footer {
          text-align: center;
          color: #999;
          font-size: 0.85rem;
          padding: 20px;
        }

        .photo-lightbox {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .lightbox-content {
          position: relative;
          max-width: 90%;
          max-height: 90%;
        }

        .lightbox-content img {
          max-width: 100%;
          max-height: 90vh;
          border-radius: 8px;
        }

        .lightbox-content .close-button {
          position: absolute;
          top: -40px;
          right: 0;
          background: none;
          border: none;
          color: white;
          font-size: 2rem;
          cursor: pointer;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          .public-report-container {
            background: white;
          }

          .report-page {
            box-shadow: none;
            page-break-after: always;
          }
        }

        @media (max-width: 768px) {
          .public-report-header .header-content {
            flex-direction: column;
            gap: 20px;
            text-align: center;
          }

          .public-report-header .company-info {
            flex-direction: column;
          }

          .public-report-header .action-buttons {
            flex-direction: column;
            width: 100%;
          }

          .public-report-header .action-buttons .btn {
            width: 100%;
          }

          .report-page {
            padding: 20px;
          }

          .cover-page {
            padding: 30px;
          }

          .cover-page .report-title {
            font-size: 1.8rem;
          }

          .findings-table {
            font-size: 0.85rem;
          }

          .findings-table th,
          .findings-table td {
            padding: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default PublicReportPage;
